import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Receipt, Info } from "lucide-react";

const FIELDS = [
  { key: "company_name", label: "Ragione sociale / Nome e Cognome", placeholder: "Es. Trattoria Da Mario SRL oppure Mario Rossi", type: "text" },
  { key: "vat_or_cf", label: "P. IVA / Codice Fiscale", placeholder: "Es. 12345678901 oppure RSSMRA80A01H501Z", type: "text", uppercase: true },
  { key: "address", label: "Indirizzo completo", placeholder: "Via Roma 12, 20100 Milano (MI)", type: "text" },
  { key: "billing_email", label: "Email di fatturazione", placeholder: "fatture@trattoriadamario.it", type: "email" },
  { key: "pec_or_sdi", label: "PEC o Codice SDI", placeholder: "Es. mariorossi@pec.it o ABC1234 (in assenza di entrambi: 0000000)", type: "text", help: "In assenza di entrambi inserisci: 0000000" },
  { key: "phone", label: "Numero di telefono", placeholder: "+39 333 1234567", type: "tel" },
];

export default function BillingInfoModal({ planKey, onClose, onSaved }) {
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map((f) => [f.key, ""])));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/billing/info");
        if (cancel) return;
        const info = r.data?.billing_info || {};
        setForm((prev) => ({ ...prev, ...Object.fromEntries(FIELDS.map((f) => [f.key, info[f.key] || ""])) }));
      } catch {
        // ignore — utente nuovo
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    // Required check
    for (const f of FIELDS) {
      if (!(form[f.key] || "").trim()) {
        toast.error(`Compila il campo "${f.label}"`);
        return;
      }
    }
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, (v || "").trim()]));
      await api.put("/billing/info", cleaned);
      toast.success("Dati di fatturazione salvati");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto p-4 sm:p-8" onClick={onClose} data-testid="billing-info-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl mx-auto my-auto shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300 flex flex-col max-h-[92vh]">
        <div className="relative px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-t-2xl flex-shrink-0">
          <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-display text-2xl text-[#0F2741]">Dati di fatturazione</h3>
              <p className="text-xs text-slate-500">{planKey ? <>Necessari per emettere fattura del piano <strong className="capitalize">{planKey}</strong>. Compila una sola volta.</> : "Modifica i tuoi dati di fatturazione."}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          {loading ? (
            <div className="py-10 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento dati...</div>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2 items-start text-xs text-blue-900">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Questi dati verranno usati per emettere la <strong>fattura elettronica</strong> del tuo abbonamento. Assicurati che siano corretti — potrai modificarli in qualsiasi momento dalle Impostazioni.</span>
              </div>

              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{f.label} <span className="text-rose-500">*</span></label>
                  <input
                    type={f.type}
                    value={form[f.key]}
                    onChange={(e) => {
                      let v = e.target.value;
                      if (f.uppercase) v = v.toUpperCase();
                      setForm({ ...form, [f.key]: v });
                    }}
                    placeholder={f.placeholder}
                    required
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-500"
                    data-testid={`billing-${f.key}`}
                  />
                  {f.help && <p className="text-[10px] text-slate-400 mt-1">{f.help}</p>}
                </div>
              ))}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex justify-between items-center flex-shrink-0">
          <span className="text-xs text-slate-500">🔒 Dati riservati · usati solo per fatturazione</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>Annulla</button>
            <button type="submit" data-testid="btn-save-billing" disabled={saving || loading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/30 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
              {saving ? "Salvataggio..." : (planKey ? "Salva e prosegui all'acquisto" : "Salva")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
